import * as React from 'react';
import { injectIntl, InjectedIntlProps } from 'react-intl';
import { withRouter, RouteComponentProps } from 'react-router';
import { compose } from 'redux';
import * as Queries from 'api/graphql/queries';
import { userList, OrderDirection } from '@api/graphql/types';
import GraphQLClient from '@api/graphql/GraphQLClient';
import ControlledTable from '@components/ControlledTable';
import UserListFilters from '../../User/components/UserListFilters';
import { RowInfo } from 'react-table';
import { Column } from 'react-virtualized';
import * as Mutations from '@api/graphql/mutations';
import { Notification } from 'components/';
import { DispatchProp, connect } from 'react-redux';
import { updateListOptions } from '@actions/listActions';
import { ListOptionType } from '@reducers/listReducer';
import { ApplicationState } from 'reducers/';

interface StateType {
    data: any[];
    paginatorInfo: any;
}

export interface UserFilterType {
    id: string;
    ext_id: string;
    name: string;
    email: string;
}

const AdminUserCreateListDefaultOptions = {
    sortBy: {
        field: 'ext_id',
        direction: OrderDirection.DESC,
    },
    count: 10,
    page: 0,
    filters: {
        id: '',
        ext_id: '',
        name: '',
        email: '',
    },
};

const listName = 'adminUserCreateList';

interface ReduxProps {
    listOptions: ListOptionType;
}

type Props = ReduxProps & DispatchProp & InjectedIntlProps & RouteComponentProps;

class AdminUserCreate extends React.Component<Props, StateType> {

    public state = {
        data: [],
        paginatorInfo: {},
    };

    private ActionCell = (row : any) => {
        if (row.original.id) {
            return (
                <button className="btn btn-xs btn-primary" onClick={this.createAdmin(row.original.id)}>
                    {this.props.intl.formatMessage({id: 'module.adminUser.grantAdmin.label'})}
                </button>
            );
        }
        return '';
    }

    private tableColumns = [
        this.getDefaultColumnByName('ext_id', {sortable: true}),
        this.getDefaultColumnByName('name', {sortable: true}),
        this.getDefaultColumnByName('email', {sortable: true}),
        this.getDefaultColumnByName('is_admin', {Cell: this.ActionCell}),
    ];

    public componentDidMount() {
        this.fetchData();
    }

    public componentDidUpdate(prevProps: Props) {
        if (prevProps.listOptions !== this.props.listOptions) {
            this.fetchData();
        }
    }

    private getDefaultColumnByName(name: string, options: any = {}) {
        return {
            Header: this.label(`${name}.title`),
            accessor: name,
            sortable: false,
            ...options,
        };
    }

    private label(id: string): string {
        return this.props.intl.formatMessage({ id: `module.adminUser.${id}` });
    }

    private getFilters() {
        const result = {...this.props.listOptions.filters};
        Object.keys(result).forEach((key) => !result[key] && delete result[key]);
        return result;
    }

    private getSortBy() {
        return {
            field: this.props.listOptions.sortBy.field.toUpperCase(),
            direction: this.props.listOptions.sortBy.direction.toUpperCase(),
        };
    }

    private createAdmin = (id: string) => (event: React.SyntheticEvent) => {
        GraphQLClient.mutate({
            mutation: Mutations.grantAdminRightToUser,
            variables: {id},
        }).then(() => {
            this.fetchData();
            return Notification.success(this.props.intl.formatMessage({ id: 'notification.succeed' }));
        }).catch((e: any) => {
            Notification.error(this.props.intl.formatMessage({ id: 'notification.failed' }));
        });
    }

    private fetchData() {
        const variables = {...this.props.listOptions, page: this.props.listOptions.page + 1, sortBy: this.getSortBy(), filters: this.getFilters()};
        GraphQLClient.query<userList>({
            query: Queries.userList,
            variables,
        }).then(response => {
            const data = response.data.listUsers ? response.data.listUsers.data : [];
            const paginatorInfo = response.data.listUsers ? response.data.listUsers.paginatorInfo : {};
            this.setState({data, paginatorInfo});
        });
    }

    private onOptionsChange = (options: ListOptionType) => {
        this.props.dispatch(updateListOptions(listName, options));
    }

    private setFilters(values: UserFilterType) {
        const { id, ext_id, name, email } = values;
        const options = {
            ...this.props.listOptions,
            filters: {
                id,
                ext_id,
                name,
                email,
            },
            page: 0,
        };
        this.onOptionsChange(options);
    }

    private onRowClick = (finalState: any, rowInfo?: RowInfo, column?: Column, instance?: any): object | undefined => {
        return {};
    }

    public render() {
        return (
            <div className="row">
                <div className="col-lg-12">
                    <div className="ibox float-e-margins">
                        <div className="ibox-title">
                            <h5>{this.props.intl.formatMessage({ id: 'module.adminUser.create.title'})}</h5>
                        </div>
                        <div className="ibox-content">
                            <UserListFilters value={this.props.listOptions.filters} onSubmit={(values: UserFilterType) => { this.setFilters(values); }} />
                            <ControlledTable
                                listName={listName}
                                defaultOptions={AdminUserCreateListDefaultOptions}
                                columns={this.tableColumns}
                                options={this.props.listOptions}
                                data={this.state.data}
                                paginatorInfo={this.state.paginatorInfo}
                                onOptionsChange={this.onOptionsChange}
                                onRowClick={this.onRowClick}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = ({ lists }: ApplicationState): ReduxProps => {
    return { listOptions: (lists ? (lists[listName] || AdminUserCreateListDefaultOptions) : AdminUserCreateListDefaultOptions) };
};

export default compose(
    withRouter,
    injectIntl,
    connect(mapStateToProps),
)(AdminUserCreate);



// WEBPACK FOOTER //
// ./src/modules/AdminUser/pages/AdminUserCreate.tsx